import isLowPriorityModality from './isLowPriorityModality';
import { ServicesManager } from '@ohif/core';

const compareSeriesDateTime = (a, b) => {
  const seriesDateA = Date.parse(`${a.seriesDate ?? a.SeriesDate} ${a.seriesTime ?? a.SeriesTime}`);
  const seriesDateB = Date.parse(`${b.seriesDate ?? b.SeriesDate} ${b.seriesTime ?? b.SeriesTime}`);

  if (seriesDateA && seriesDateB) {
    return seriesDateA - seriesDateB;
  }
  const rawDateA = a.seriesDate ?? a.SeriesDate; //ex. '20240831'
  const rawDateB = b.seriesDate ?? b.SeriesDate;

  // Format into ISO 8601: ex '2024-08-31T10:21:48.432'
  if (rawDateA && rawDateB) {
    const formattedDateA = `${rawDateA.slice(0, 4)}-${rawDateA.slice(4, 6)}-${rawDateA.slice(6, 8)}`;
    const formattedDateB = `${rawDateB.slice(0, 4)}-${rawDateB.slice(4, 6)}-${rawDateB.slice(6, 8)}`;
    const isoStringA = `${formattedDateA}`;
    const newSeriesDateA = Date.parse(isoStringA);

    const isoStringB = `${formattedDateB}`;
    const newSeriesDateB = Date.parse(isoStringB);

    return newSeriesDateA - newSeriesDateB;
  }

  return null;
};

const defaultSeriesSort = (a, b, servicesManager = {}) => {
  const displaySetService = services?.displaySetService; //Note displaySets are different than Thumbnails
  //a and b are displaySets!
  //get all displaySets within series a and series b, then sort within a and within b
  //Then sort between the series
  //If the displaySets are from the same series, then get all others from within that series and sort


  // Primary sorting: Use sortVector if available
  const vectorCompare = compareSortVector(a, b);
  if (vectorCompare !== 0) {
    return vectorCompare;
  }


  const aIsUS = (a.modality ?? a.Modality) === 'US';
  const bIsUS = (b.modality ?? b.Modality) === 'US';

  if (aIsUS && bIsUS) {
    // Both are US → order by AcquisitionNumber or InstanceNumber
    const acqA = parseInt(a.instanceNumber ?? 0);
    const acqB = parseInt(b.instanceNumber ?? 0);

    if (acqA !== acqB) {
      return acqA - acqB;
    }
  }

  // ✅ If only one is US, push US sets after non-US
  if (aIsUS && !bIsUS) {
    return 1;
  }
  if (!aIsUS && bIsUS) {
    return -1;
  }

  // 🔽 Otherwise: fall back to existing logic

  const seriesNumberA = a.SeriesNumber ?? a.seriesNumber;
  const seriesNumberB = b.SeriesNumber ?? b.seriesNumber;

  if (seriesNumberA === seriesNumberB) {
    //both displaySet thumbnails are from the SAME series
    if (a.displaySetInstanceUID && b.displaySetInstanceUID) {
      const displaySetA = displaySetService.getDisplaySetByUID(a.displaySetInstanceUID); //get the displaySet from the Thumbnail
      const seriesUID = displaySetA?.SeriesInstanceUID; //get the seriesUID so we can find all displaySets associated with that series

      if (seriesUID) {
        const displaySets = displaySetService.getDisplaySetsForSeries(seriesUID); //get all displaySets from the same series so we can then sort them

        if (displaySets?.length > 1) {
          // Sort displaySets within the series using sortVector
          const sortedDisplaySets = [...displaySets].sort((dsA, dsB) =>
            compareSortVector(dsA, dsB)
          );

          // Determine the index positions of a and b
          const indexA = sortedDisplaySets.findIndex(
            ds => ds.displaySetInstanceUID === a.displaySetInstanceUID
          );
          const indexB = sortedDisplaySets.findIndex(
            ds => ds.displaySetInstanceUID === b.displaySetInstanceUID
          );
          return indexA - indexB;
        }
      }
    }
  }

  const dateCompare = compareSeriesDateTime(a, b);
  if (dateCompare !== null && dateCompare !== undefined) {
    return dateCompare;
  }

  //different series
  const seriesNumberCompare = compare(seriesNumberA, seriesNumberB);
  if (seriesNumberCompare !== 0 && seriesNumberCompare !== null) {
    return seriesNumberCompare;
  }
  const dateTimeCompare = compareSeriesDateTime(a, b);
  if (dateTimeCompare !== null && dateTimeCompare !== undefined && dateTimeCompare !== 0) {
    return dateTimeCompare;
  }
  const seriesCompare = compare(a.SeriesInstanceUID, b.SeriesInstanceUID);
  if (seriesCompare !== 0 && seriesCompare !== null) {
    return seriesCompare;
  }

  if (a.displaySetInstanceUID && b.displaySetInstanceUID) {
    return compare(a.displaySetInstanceUID, b.displaySetInstanceUID);
  }
};

/**
 * Series sorting criteria: series considered low priority are moved to the end
 * of the list and series number is used to break ties
 * @param {Object} firstSeries
 * @param {Object} secondSeries
 */
function seriesInfoSortingCriteria(firstSeries, secondSeries) {
  const aLowPriority = isLowPriorityModality(firstSeries.Modality ?? firstSeries.modality);
  const bLowPriority = isLowPriorityModality(secondSeries.Modality ?? secondSeries.modality);

  if (aLowPriority) {
    // Use the reverse sort order for low priority modalities so that the
    // most recent one comes up first as usually that is the one of interest.
    return bLowPriority ? defaultSeriesSort(secondSeries, firstSeries) : 1;
  } else if (bLowPriority) {
    return -1;
  }

  return defaultSeriesSort(firstSeries, secondSeries);
}

const seriesSortCriteria = {
  default: seriesInfoSortingCriteria,
  seriesInfoSortingCriteria,
};

const compare = (a, b) => {
  if (a === b) {
    return 0;
  }
  const aI = parseFloat(a);
  const bI = parseFloat(b);
  if (!isNaN(aI) && !isNaN(bI)) {
    a = aI;
    b = bI;
  }
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

function getSortVector(displaySet, defaultSortPosition = 0) {
  return Array.isArray(displaySet?.sortVector) ? displaySet.sortVector : [defaultSortPosition];
}
// function compareSortVector(a, b) {
//   const aV = getSortVector(a);
//   const bV = getSortVector(b);

//   for (let i = 0; i < aV.length && i < bV.length; i++) {
//     const aVi = aV[i];
//     const bVi = bV[i];
//     const c = compare(aVi, bVi);
//     if (c !== 0) {
//       //continue comparing if they're equal
//       return -c; //negative because we want to sort from base to apex (-), not apex to base (+)
//     }
//   }
//   return bV.length - aV.length;
// }
function compareSortVector(a, b) {
  const aV = getSortVector(a);
  const bV = getSortVector(b);

  // If both have empty sort vectors, consider them equal for now
  if (aV.length === 0 && bV.length === 0) {
    return 0;
  }

  // Compare element by element
  for (let i = 0; i < Math.max(aV.length, bV.length); i++) {
    const aVi = aV[i] ?? 0; // Default to 0 if vector is shorter
    const bVi = bV[i] ?? 0;

    const c = compare(aVi, bVi);
    if (c !== 0) {
      return c;
    }
  }

  // If sort vectors are identical, fall back to other criteria
  return 0;
}

const instancesSortCriteria = {
  default: (a, b, servicesManager = {}) => {
    const displaySetService = services?.displaySetService;
    const instanceCompare = parseInt(a.InstanceNumber) - parseInt(b.InstanceNumber);
    if (instanceCompare !== 0 && instanceCompare !== null) {
      return instanceCompare;
    }
    const sopCompare = compare(a.SOPInstanceUID, b.SOPInstanceUID);
    if (sopCompare !== 0 && sopCompare !== null) {
      return sopCompare;
    }
    const vectorCompare = compareSortVector(
      displaySetService.getDisplaySet(a.displaySetInstanceUID),
      displaySetService.getDisplaySet(b.displaySetInstanceUID)
    );
    if (vectorCompare !== 0 && vectorCompare !== null) {
      return vectorCompare;
    }
    return 0;
  },
};

const sortingCriteria = {
  seriesSortCriteria,
  instancesSortCriteria,
};

/**
 * Sorts given series (given param is modified)
 * The default criteria is based on series number in ascending order.
 *
 * @param {Array} series List of series
 * @param {function} seriesSortingCriteria method for sorting
 * @returns {Array} sorted series object
 */
const sortStudySeries = (
  series,
  seriesSortingCriteria = seriesSortCriteria.default,
  sortFunction = null
) => {
  if (typeof sortFunction === 'function') {
    return sortFunction(series);
  } else {
    return series.sort(seriesSortingCriteria);
  }
};

/**
 * Sorts given instancesList (given param is modified)
 * The default criteria is based on instance number in ascending order.
 *
 * @param {Array} instancesList List of series
 * @param {function} instancesSortingCriteria method for sorting
 * @returns {Array} sorted instancesList object
 */

const sortStudyInstances = (
  instancesList,
  instancesSortingCriteria = instancesSortCriteria.default,
  servicesManager = {}
) => {
  return instancesList.sort((a, b) => instancesSortingCriteria(a, b, servicesManager));
};
/**
 * Sorts the series and instances (by default) inside a study instance based on sortingCriteria (given param is modified)
 * The default criteria is based on series and instance numbers in ascending order.
 *
 * @param {Object} study The study instance
 * @param {boolean} [deepSort = true] to sort instance also
 * @param {function} [seriesSortingCriteria = seriesSortCriteria.default] method for sorting series
 * @param {function} [instancesSortingCriteria = instancesSortCriteria.default] method for sorting instances
 * @returns {Object} sorted study object
 */
export default function sortStudy(
  study,
  deepSort = true,
  seriesSortingCriteria = seriesSortCriteria.default,
  instancesSortingCriteria = instancesSortCriteria.default,
  servicesManager = {}
) {
  if (!study || !study.series) {
    throw new Error('Insufficient study data was provided to sortStudy');
  }

  sortStudySeries(study.series, seriesSortingCriteria);

  if (deepSort) {
    study.series.forEach(series => {
      sortStudyInstances(series.instances, instancesSortingCriteria, servicesManager);
    });
  }

  return study;
}

export { sortStudy, sortStudySeries, sortStudyInstances, sortingCriteria, seriesSortCriteria };
